import { useEffect, useState } from "react";

export type TourTarget = {
  element: Element;
  rect: DOMRect;
};

const SHADOW_COMBINATOR = ">>>";

const TALL_TARGET_RATIO = 0.6;

const TALL_TARGET_INSET = 0.18;

// An island renders into its own shadow root, which querySelector will not
// cross. `#table >>> [data-slot="row"]` says which host to open on the way.

function resolveTarget(selector: string): Element | null {
  let root: ParentNode = document;
  let element: Element | null = null;

  for (const part of selector.split(SHADOW_COMBINATOR)) {
    element = root.querySelector(part.trim());

    if (!element) {
      return null;
    }

    root = element.shadowRoot ?? element;
  }

  return element;
}

function clipRect(rect: DOMRect, cover: DOMRect): DOMRect {
  const overlapsHorizontally =
    cover.left < rect.right && cover.right > rect.left;
  const overlapsVertically = cover.top < rect.bottom && cover.bottom > rect.top;

  if (!overlapsHorizontally || !overlapsVertically) {
    return rect;
  }

  const band = (x: number, y: number, width: number, height: number) =>
    new DOMRect(x, y, Math.max(0, width), Math.max(0, height));

  const bands = [
    band(rect.x, rect.y, cover.left - rect.left, rect.height),
    band(cover.right, rect.y, rect.right - cover.right, rect.height),
    band(rect.x, rect.y, rect.width, cover.top - rect.top),
    band(rect.x, cover.bottom, rect.width, rect.bottom - cover.bottom),
  ];

  return bands.reduce((widest, band) =>
    band.width * band.height > widest.width * widest.height ? band : widest,
  );
}

function isFullyVisible(rect: DOMRect): boolean {
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= window.innerHeight &&
    rect.right <= window.innerWidth
  );
}

function scrollTargetIntoView(element: Element): void {
  const rect = element.getBoundingClientRect();

  if (isFullyVisible(rect)) {
    return;
  }

  if (
    rect.height > window.innerHeight * TALL_TARGET_RATIO &&
    element instanceof HTMLElement
  ) {
    const inset = Math.round(window.innerHeight * TALL_TARGET_INSET);
    const previous = element.style.scrollMarginTop;

    element.style.scrollMarginTop = `${inset}px`;
    element.scrollIntoView({ block: "start", inline: "nearest" });
    element.style.scrollMarginTop = previous;

    return;
  }

  element.scrollIntoView({ block: "center", inline: "center" });
}

export function useTourTarget(
  selector: string | undefined,
  active: boolean,
  clip?: string,
): TourTarget | null {
  const [rect, setRect] = useState<DOMRect | null>(null);

  const element = active && selector ? resolveTarget(selector) : null;

  useEffect(() => {
    if (!active || !selector) {
      return;
    }

    const element = resolveTarget(selector);

    if (!element) {
      return;
    }

    scrollTargetIntoView(element);

    let frame = 0;
    let previous = "";

    function track(tracked: Element) {
      const cover = clip ? resolveTarget(clip) : null;
      const measured = tracked.getBoundingClientRect();
      const rect = cover
        ? clipRect(measured, cover.getBoundingClientRect())
        : measured;
      const signature = `${rect.x},${rect.y},${rect.width},${rect.height}`;

      if (signature !== previous) {
        previous = signature;

        setRect(rect);
      }

      frame = requestAnimationFrame(() => track(tracked));
    }

    frame = requestAnimationFrame(() => track(element));

    return () => cancelAnimationFrame(frame);
  }, [selector, active, clip]);

  return element && rect ? { element, rect } : null;
}
